// The (model → tier) tagging state of one capture, split from
// `createKeyCapture` the way `createKeyRotate` is split from the key routes.
//
// A `createStore` rather than a signal over a Map: a provider catalogue is up to
// ~300 rows and every row's select reads its own tier, so a store patches ONE
// entry per pick and re-renders only that row. Untagging DELETES the key rather
// than writing `undefined` — `taggedModels` reads `Object.entries`, and a
// lingering key would keep counting against the server's 50-entry ceiling.

import type { BYOKTier } from '@developerz/domain';
import { createStore, produce } from 'solid-js/store';
import { MODEL_TAG_MAX, type ModelTags } from './keyModels';

export type ModelTagsController = {
  /** The tags themselves — pass to `taggedModels` to build the POST payload. */
  readonly tags: ModelTags;
  readonly tierOf: (model: string) => BYOKTier | undefined;
  /** Tag a model, or untag it with `null`. Refused at the ceiling (see `atCap`). */
  readonly setTier: (model: string, tier: BYOKTier | null) => void;
  readonly count: () => number;
  /** At the server's ceiling: the UI shuts the UNTAGGED rows' selects, never all of them. */
  readonly atCap: () => boolean;
  readonly clear: () => void;
};

export function createModelTags(): ModelTagsController {
  const [tags, setTags] = createStore<Record<string, BYOKTier | undefined>>({});

  const count = (): number => Object.keys(tags).length;

  return {
    tags,
    tierOf: (model) => tags[model],
    setTier: (model, tier) => {
      if (tier === null) {
        setTags(
          produce((draft) => {
            delete draft[model];
          }),
        );
        return;
      }
      // The cap binds only NEW entries: re-tiering a model already tagged does
      // not grow the list, and refusing it would strand the reader at 50 with a
      // pick they cannot change.
      if (tags[model] === undefined && count() >= MODEL_TAG_MAX) return;
      setTags(model, tier);
    },
    count,
    atCap: () => count() >= MODEL_TAG_MAX,
    clear: () =>
      setTags(
        produce((draft) => {
          for (const model of Object.keys(draft)) delete draft[model];
        }),
      ),
  };
}
